/**
 * La exportación para la gestoría — `OD-0036` (`ACCEPTED` 2026-10-18), Business
 * (`OD-0042 D-04`, feature `accountant_export`).
 *
 * Un panel que se abre con el botón «Exportar para la gestoría», junto a
 * «Nuevo borrador», y se pinta ENCIMA de la lista: se elige el periodo y el
 * formato (Excel, CSV o el libro registro de la AEAT) y se descarga el listado
 * o el paquete con los PDF (ZIP), que lleva dentro el listado en ese formato.
 *
 * Antes era una tarjeta debajo de la lista; con muchas facturas quedaba al
 * final de la página y no se veía (propietario, 2026-10-22).
 *
 * ## Lo que esta pantalla NO decide
 *
 * Ni si el plan la incluye —`incluidoEnElPlan` llega resuelto de la API, y
 * `URS §105` prohíbe comparar planes en el navegador— ni qué trimestres hay: la
 * API manda la lista ya calculada con el día de Madrid. La web no decide fechas
 * fiscales, igual que no calcula importes.
 *
 * Sin el plan, el botón sigue ahí y el panel se abre con el aviso y el enlace a
 * los planes: `OD-0036` pide «el botón bloqueado, sin ocultarlo».
 *
 * ## Tres sitios, tres alcances (`OD-0054-ENMIENDA-01`, 2026-10-24)
 *
 * ```text
 * FACTURAS           "ventas"   las emitidas: listado (Excel, CSV, AEAT) y ZIP;
 *                               y, si la API lo ofrece, los libros para
 *                               ContaSOL (OD-0063 D-10f)
 * COMPRAS            "compras"  solo las recibidas: listado en Excel o CSV,
 *                               y el paquete con los adjuntos (OD-0063 D-10e)
 * INFORMES FISCALES  "todo"     las dos, con el trimestre del informe, y
 *                               además las dos JUNTAS en un Excel con una
 *                               pestaña cada una (propietario, 2026-10-24)
 * ```
 *
 * Las recibidas sólo se ofrecen si la API dice `veCompras` (`OD-0054 D-15`):
 * SALES exporta sus facturas, pero no ve lo que se paga a los proveedores. Y no
 * en el formato de la AEAT, que para recibidas todavía no existe (`D-14`).
 *
 * El estado (plan, trimestres, `veCompras`) lo pide cada pantalla con
 * `useEstadoExportacion`: el botón sólo se pinta si la API lo ha dado.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

import { api, type EstadoExportacion, type FormatoListado, type FormatoRecibidas, type OpcionesContasol, type PeriodoExportacion } from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Campo, Selector } from "../ui/primitivas";
import { ComentarioDelTrimestre } from "./TarjetaNotaAsesoria";

type Modo = "trimestre" | "rango";

export type AlcanceExportacion = "ventas" | "compras" | "todo";

/**
 * El estado de la exportación, o `null` mientras no llega o si la API no lo da
 * (sin permiso, sin red): entonces no hay botón, y la pantalla sigue a lo suyo.
 */
export function useEstadoExportacion(): EstadoExportacion | null {
  const [estado, setEstado] = useState<EstadoExportacion | null>(null);
  useEffect(() => {
    let vivo = true;
    void api.estadoExportacion().then((r) => {
      if (vivo && r.ok) setEstado(r.datos);
    });
    return () => {
      vivo = false;
    };
  }, []);
  return estado;
}

/** El botón que abre y cierra el panel. Sin el plan sigue aquí: el panel lo explica (`OD-0036`). */
export function BotonExportacion({ abierto, onClick }: { readonly abierto: boolean; readonly onClick: () => void }) {
  return (
    <button type="button" className="boton boton-secundario" aria-expanded={abierto} aria-controls="panel-exportacion" onClick={onClick}>
      {t.exportacion.boton}
    </button>
  );
}

/** Entrega el fichero al navegador como una descarga normal, con su nombre. */
function guardar(contenido: Blob, nombre: string): void {
  const url = URL.createObjectURL(contenido);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // En el siguiente turno: revocarla en el mismo instante cancela la descarga
  // en algunos navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** El marco del panel: título con el foco al abrirse, y «Cerrar». */
function Marco({ onCerrar, children }: { readonly onCerrar: () => void; readonly children: ReactNode }) {
  const titulo = useRef<HTMLHeadingElement>(null);
  // Al abrirse, el foco (y la vista, en el móvil) va al panel: si no, quien usa
  // teclado o lector de pantalla no se entera de que ha aparecido.
  useEffect(() => {
    titulo.current?.focus();
  }, []);
  return (
    <section id="panel-exportacion" className="panel-exportacion" aria-labelledby="panel-exportacion-titulo">
      <div className="panel-exportacion-cabecera">
        <h3 id="panel-exportacion-titulo" ref={titulo} tabIndex={-1}>
          {t.exportacion.titulo}
        </h3>
        <Boton variante="terciario" onClick={onCerrar}>
          {t.exportacion.cerrar}
        </Boton>
      </div>
      {children}
    </section>
  );
}

export function PanelExportacion({
  estado,
  hayConexion,
  onCerrar,
  alcance = "ventas",
  trimestreInicial = null,
}: {
  readonly estado: EstadoExportacion;
  readonly hayConexion: boolean;
  readonly onCerrar: () => void;
  readonly alcance?: AlcanceExportacion;
  /** Informes fiscales abre el panel con el trimestre que se está mirando. */
  readonly trimestreInicial?: string | null;
}) {
  const [modo, setModo] = useState<Modo>("trimestre");
  const [trimestre, setTrimestre] = useState(
    trimestreInicial !== null && estado.trimestres.includes(trimestreInicial) ? trimestreInicial : (estado.trimestres[0] ?? ""),
  );
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  // Excel por defecto: es lo que importan los programas de las gestorías con
  // una plantilla, y lo que abre cualquiera sin asistente de importación.
  const [formato, setFormato] = useState<FormatoListado>("xlsx");
  const [ocupado, setOcupado] = useState<"listado" | "zip" | "recibidas" | "recibidasZip" | "completo" | "contasol" | null>(null);
  // Los valores de siempre de ContaSOL; la gestoría los cambia si su plan de cuentas es otro.
  const [contasol, setContasol] = useState<OpcionesContasol>({ digitos: "10", prefijoClientes: "430", prefijoProveedores: "400", primerCodigo: "1" });
  const [error, setError] = useState<string | null>(null);

  const conVentas = alcance !== "compras";
  const conCompras = alcance !== "ventas" && estado.veCompras;
  // Las dos juntas solo donde se ofrecen las dos, y solo a quien ve las dos.
  const conCompleto = conVentas && conCompras;

  if (!estado.incluidoEnElPlan) {
    return (
      <Marco onCerrar={onCerrar}>
        <Aviso tono="aviso">{alcance === "ventas" ? t.exportacion.sinPlan : t.exportacion.sinPlanCompras}</Aviso>
        <a className="boton boton-secundario enlace-boton" href="/plan">
          {t.exportacion.verPlanes}
        </a>
      </Marco>
    );
  }

  const periodo: PeriodoExportacion | null =
    modo === "trimestre" ? (trimestre === "" ? null : { trimestre }) : desde === "" || hasta === "" ? null : { desde, hasta };
  // Las recibidas no tienen libro de la AEAT todavía (D-14): con ese formato no se piden.
  const formatoRecibidas: FormatoRecibidas | null = formato === "aeat" ? null : formato;

  const descargar = async (que: "listado" | "zip" | "recibidas" | "recibidasZip" | "completo" | "contasol"): Promise<void> => {
    if (periodo === null) return;
    setError(null);
    setOcupado(que);
    const r =
      que === "completo"
        ? await api.descargarCompleto(periodo)
        : que === "contasol"
          ? await api.descargarContasol(periodo, contasol)
          : que === "recibidasZip"
          ? await api.descargarRecibidasZip(periodo)
          : que === "recibidas"
            ? formatoRecibidas === null
              ? null
              : await api.descargarRecibidas(formatoRecibidas, periodo)
            : await api.descargarExportacion(formato, que === "zip", periodo);
    setOcupado(null);
    if (r === null) return;
    if (!r.ok) {
      // El mensaje de la API ya viene escrito para leerse, con lo que hay que
      // hacer (p. ej. «descarga el paquete por meses»).
      setError(r.error === null ? t.exportacion.error : [r.error.message, r.error.action].filter((x) => x !== null).join(" "));
      return;
    }
    guardar(r.datos.contenido, r.datos.nombre);
  };

  const deshabilitado = !hayConexion || periodo === null || ocupado !== null;
  const formatos: readonly FormatoListado[] = conVentas ? ["xlsx", "csv", "aeat"] : ["xlsx", "csv"];
  return (
    <Marco onCerrar={onCerrar}>
      <p className="ayuda-neutra">{t.exportacion.detalles[alcance === "todo" && !conCompras ? "ventas" : alcance]}</p>
      {/* En una fila en el escritorio: a todo el ancho, tres desplegables de
          1.700 px para elegir un trimestre (auditoria de diseno, 2026-10-19). */}
      <div className="fila-campos exportacion-campos">
      <Selector<Modo>
        id="exportacion-modo"
        etiqueta={t.exportacion.periodo}
        valor={modo}
        opciones={[
          { valor: "trimestre", texto: t.exportacion.porTrimestre },
          { valor: "rango", texto: t.exportacion.porFechas },
        ]}
        onChange={setModo}
      />
      {modo === "trimestre" ? (
        <Selector<string>
          id="exportacion-trimestre"
          etiqueta={t.exportacion.trimestre}
          valor={trimestre}
          opciones={estado.trimestres.map((x) => ({ valor: x, texto: t.exportacion.nombreTrimestre(x) }))}
          onChange={setTrimestre}
        />
      ) : (
        <>
          <Campo id="exportacion-desde" etiqueta={t.exportacion.desde} tipo="date" valor={desde} onChange={setDesde} />
          <Campo
            id="exportacion-hasta"
            etiqueta={t.exportacion.hasta}
            tipo="date"
            valor={hasta}
            onChange={setHasta}
            ayuda={t.exportacion.ayudaRango(estado.diasMaximosDeRango)}
          />
        </>
      )}
      <Selector<FormatoListado>
        id="exportacion-formato"
        etiqueta={t.exportacion.formato}
        valor={formato}
        opciones={formatos.map((f) => ({ valor: f, texto: t.exportacion.formatos[f] }))}
        onChange={setFormato}
      />
      </div>
      <p className="ayuda-neutra">{t.exportacion.ayudaFormato[formato]}</p>
      {/* OD-0067 D-03: el comentario viaja solo con un TRIMESTRE; con un rango no se ofrece. */}
      {modo === "trimestre" && trimestre !== "" ? (
        <ComentarioDelTrimestre key={trimestre} trimestre={trimestre} nombre={t.exportacion.nombreTrimestre(trimestre)} hayConexion={hayConexion} />
      ) : null}
      {error !== null ? <Aviso tono="error">{error}</Aviso> : null}
      {conCompleto ? (
        <div className="exportacion-bloque">
          <h4>{t.exportacion.juntas}</h4>
          <div className="acciones">
            <Boton onClick={() => void descargar("completo")} deshabilitado={deshabilitado}>
              {ocupado === "completo" ? t.exportacion.preparando : t.exportacion.completo}
            </Boton>
          </div>
          <p className="ayuda-neutra">{t.exportacion.ayudaCompleto}</p>
        </div>
      ) : null}
      {conVentas ? (
        <div className="exportacion-bloque">
          {alcance === "todo" ? <h4>{t.exportacion.ventas}</h4> : null}
          <div className="acciones">
            <Boton variante={conCompleto ? "secundario" : "principal"} onClick={() => void descargar("listado")} deshabilitado={deshabilitado}>
              {ocupado === "listado" ? t.exportacion.preparando : t.exportacion.listado}
            </Boton>
            <Boton variante="secundario" onClick={() => void descargar("zip")} deshabilitado={deshabilitado}>
              {ocupado === "zip" ? t.exportacion.preparando : t.exportacion.paquete}
            </Boton>
          </div>
          <p className="ayuda-neutra">{t.exportacion.ayudaPaquete(estado.maxFacturasPorZip)}</p>
        </div>
      ) : null}
      {conVentas && estado.contasol ? (
        <div className="exportacion-bloque">
          <h4>{t.exportacion.contasol}</h4>
          <div className="fila-campos exportacion-campos">
            <Selector<string>
              id="exportacion-contasol-digitos"
              etiqueta={t.exportacion.contasolDigitos}
              valor={contasol.digitos}
              opciones={["8", "9", "10"].map((d) => ({ valor: d, texto: d }))}
              onChange={(digitos) => setContasol({ ...contasol, digitos })}
            />
            <Campo
              id="exportacion-contasol-clientes"
              etiqueta={t.exportacion.contasolPrefijoClientes}
              valor={contasol.prefijoClientes}
              inputMode="numeric"
              onChange={(prefijoClientes) => setContasol({ ...contasol, prefijoClientes })}
            />
            <Campo
              id="exportacion-contasol-proveedores"
              etiqueta={t.exportacion.contasolPrefijoProveedores}
              valor={contasol.prefijoProveedores}
              inputMode="numeric"
              onChange={(prefijoProveedores) => setContasol({ ...contasol, prefijoProveedores })}
            />
            <Campo
              id="exportacion-contasol-codigo"
              etiqueta={t.exportacion.contasolPrimerCodigo}
              valor={contasol.primerCodigo}
              inputMode="numeric"
              onChange={(primerCodigo) => setContasol({ ...contasol, primerCodigo })}
            />
          </div>
          <div className="acciones">
            <Boton variante="secundario" onClick={() => void descargar("contasol")} deshabilitado={deshabilitado}>
              {ocupado === "contasol" ? t.exportacion.preparando : t.exportacion.contasolDescargar}
            </Boton>
          </div>
          <p className="ayuda-neutra">{t.exportacion.ayudaContasol}</p>
        </div>
      ) : null}
      {conCompras ? (
        <div className="exportacion-bloque">
          {alcance === "todo" ? <h4>{t.exportacion.compras}</h4> : null}
          <div className="acciones">
            <Boton
              variante={alcance === "compras" ? "principal" : "secundario"}
              onClick={() => void descargar("recibidas")}
              deshabilitado={deshabilitado || formatoRecibidas === null}
            >
              {ocupado === "recibidas" ? t.exportacion.preparando : t.exportacion.recibidas}
            </Boton>
            {/* El paquete lleva Excel y CSV dentro, sea cual sea el formato elegido. */}
            {estado.paqueteDeRecibidas !== null ? (
              <Boton variante="secundario" onClick={() => void descargar("recibidasZip")} deshabilitado={deshabilitado}>
                {ocupado === "recibidasZip" ? t.exportacion.preparando : t.exportacion.paqueteRecibidas}
              </Boton>
            ) : null}
          </div>
          <p className="ayuda-neutra">{formatoRecibidas === null ? t.exportacion.recibidasSinAeat : t.exportacion.ayudaRecibidas}</p>
          {estado.paqueteDeRecibidas !== null ? (
            <p className="ayuda-neutra">{t.exportacion.ayudaPaqueteRecibidas(estado.paqueteDeRecibidas.maxCompras, estado.paqueteDeRecibidas.maxMb)}</p>
          ) : null}
        </div>
      ) : null}
    </Marco>
  );
}
